/**
 * Simple async request queue with configurable concurrency.
 *
 * Queued functions only start (and therefore only begin their timeout clocks)
 * when a concurrency slot opens up. This prevents slow or concurrent-heavy
 * operations from timing out while waiting for the server to become available.
 */
class RequestQueue {
  constructor(concurrency = 3) {
    this.concurrency = concurrency;
    this.running = 0;
    this.queue = [];
  }

  /**
   * Add a function to the queue. The function is called only when a slot is free.
   * @param {() => Promise<any>} fn - The async function to run (e.g. a fetch call).
   * @returns {Promise<any>} Resolves/rejects with the result of fn().
   */
  enqueue(fn) {
    return new Promise((resolve, reject) => {
      this.queue.push({ fn, resolve, reject });
      this._drain();
    });
  }

  /** Returns the number of requests currently waiting in the queue (not yet started). */
  get pending() {
    return this.queue.length;
  }

  _drain() {
    while (this.running < this.concurrency && this.queue.length > 0) {
      const { fn, resolve, reject } = this.queue.shift();
      this.running++;
      Promise.resolve()
        .then(() => fn())
        .then(resolve, reject)
        .finally(() => {
          this.running--;
          this._drain();
        });
    }
  }
}

/**
 * Shared queue for slow/expensive API calls (deep dives, events, SEO, market share, trends).
 * Max 3 concurrent — enough to feel responsive without saturating a Railway dyno.
 */
export const slowRequestQueue = new RequestQueue(3);

/**
 * Separate queue for glossary definitions (lightweight but can pile up rapidly).
 * Max 2 concurrent so the lite model isn't hammered when a user adds several terms at once.
 */
export const glossaryQueue = new RequestQueue(2);
